import React, { useReducer, useEffect, useImperativeHandle } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { SubmissionError } from 'redux-form';

import {
  ProductionRecordDetailFieldEnum as ProdRecordDetailFields,
  RecipeFieldEnum as RecipeFields,
} from 'Common/FieldEnum';
import * as ProdRecordHelpers from 'Pages/manageproduction/helpers/productionRecord';
import {
  ProductionRecordFieldEnum as ProdRecordFields,
  ProductionRecipeLeftoverEnum as ProdRecipeLeftoverFields,
} from 'Common/FieldEnum';
import * as FormHelpers from 'Pages/manageproduction/helpers/form';
import * as FormSelectors from 'Pages/manageproduction/helpers/formSelectors';
import { createToast, toastType } from 'Common/ToastHelper';
import { MESSAGES as CycleMenuMessages } from 'Pages/managemenu/helpers/Constants';
import { hasValidServingSize } from 'Pages/managemenu/subpages/editmenu/utilities';
import { Toolbox } from 'Components/toolbox/Toolbox';
import * as EditProductionActions from 'Pages/manageproduction/state/editproduction/actions';
import * as EditProductionSelectors from 'Pages/manageproduction/state/editproduction/selectors';
import { Modals } from 'Pages/manageproduction/helpers/Constants';
import * as CommonStateSelectors from 'State/selectors';

import { ProductionGrid } from './ProductionGrid';
import { ProductionPlanModal } from './ProductionPlanModal';
import { validate as closeProdFormValidate } from './CloseProductionForm';

const ActionTypes = {
  Initialize: 'Initialize',
  Add: 'Add',
  Delete: 'Delete',
  Change: 'Change',
  UpdateGrid: 'UpdateGrid',
  AddProductionPlanChanges: 'AddProductionPlanChanges',
};

const EditProductionFormContext = React.createContext();
const EditProductionFormActionsContext = React.createContext();

function reducer(state, action) {
  const { type, payload } = action;

  switch (type) {
    case ActionTypes.Add:
      return { ...state, ...FormHelpers.addRecipe(state, payload) };
    case ActionTypes.Change:
      return {
        ...state,
        ...FormHelpers.updateForm(state, payload),
      };
    case ActionTypes.Delete:
      return { ...state, ...FormHelpers.deleteDetail(state, payload) };
    case ActionTypes.UpdateGrid:
      return {
        ...state,
        ...FormHelpers.updateGrid(state, payload),
      };
    case ActionTypes.ToggleModal: {
      const { modal, show } = payload;
      return { ...state, [modal]: show };
    }
    case ActionTypes.AddProductionPlanChanges:
      return {
        ...state,
        ...payload,
      };
    case ActionTypes.Initialize: {
      const { prodRecords, detailsByRecipeCategory } = payload;
      return { ...state, prodRecords, detailsByRecipeCategory };
    }
    default:
      return new Error(`No handler for action type ${type}.`);
  }
}

function EditProductionForm(props) {
  const {
    isReadOnly,
    prodRecords,
    viewProdRecordIndex,
    recipeToAdd,
    expandedRecipesByCategory,
    toolboxProps,
    saveProdRecords,
    closeProdRecord,
    showProdPlanModal,
    showCloseModal,
    changeModalVisiblity,
    updatePlanProductionValues,
    removeAddedLeftovers,
    clearRecipeToAdd,
    forwardRef,
    userName,
  } = props;
  const [state, dispatch] = useReducer(reducer, {
    prodRecords: _.cloneDeep(prodRecords),
    detailsByRecipeCategory: ProdRecordHelpers.groupDetailsByRecipeCategory(
      _.get(prodRecords, [viewProdRecordIndex, ProdRecordFields.Details], []),
      expandedRecipesByCategory
    ),
    viewProdRecordIndex,
    expandedRecipesByCategory,
  });

  // Expose form values on ref
  useImperativeHandle(forwardRef, () => ({
    prodRecords: FormSelectors.getProdRecords(state),
  }));

  // Handles reinitializing the form for scenarios such as closing and starting over
  useEffect(() => {
    dispatch({
      type: ActionTypes.Initialize,
      payload: {
        prodRecords: _.cloneDeep(prodRecords),
        detailsByRecipeCategory: ProdRecordHelpers.groupDetailsByRecipeCategory(
          _.get(
            prodRecords,
            [viewProdRecordIndex, ProdRecordFields.Details],
            []
          ),
          expandedRecipesByCategory
        ),
      },
    });
  }, [prodRecords]);

  // Handle updating which items need to be displayed. i.e. day change or new recipe expanded
  useEffect(() => {
    dispatch({
      type: ActionTypes.UpdateGrid,
      payload: {
        prodRecords,
        viewProdRecordIndex,
        expandedRecipesByCategory,
      },
    });
  }, [
    expandedRecipesByCategory,
    viewProdRecordIndex,
    prodRecords[viewProdRecordIndex].FeedingFigure,
  ]);

  // Handle adding recipe to production record
  useEffect(() => {
    // Clear recipeToAdd otherwise will run into cases where it will be added again if the
    // form is remounted
    clearRecipeToAdd();
    if (recipeToAdd) {
      if (!hasValidServingSize(recipeToAdd)) {
        const { Title, Message } =
          CycleMenuMessages.ADD_RECIPE_INVALID_SERVING_SIZE;
        createToast(toastType.error, Title, Message);
        return;
      }
      const details = FormSelectors.getProdDetails(state);
      const isLeftover = Boolean(
        recipeToAdd[ProdRecipeLeftoverFields.Leftovers]
      );

      const hasRecipe =
        details.filter(
          (d) =>
            !ProdRecordHelpers.isLeftover(d) &&
            d[ProdRecordDetailFields.RecipeId] ===
              recipeToAdd[RecipeFields.Id] &&
            !ProdRecordHelpers.isDetailIngredient(d)
        ).length > 0;

      if (hasRecipe && !isLeftover) {
        createToast(toastType.error, 'Error', 'Recipe has already been added.');
        return;
      }

      dispatch({
        type: ActionTypes.Add,
        payload: { recipe: recipeToAdd, isLeftover },
      });

      if (isLeftover) {
        removeAddedLeftovers(recipeToAdd);
      }
    }
  }, [recipeToAdd]);

  const updateForm = (formValues) => {
    var updatedProductionRecords = FormHelpers.addProductionPlanChanges(state, {
      formValues,
    });
    dispatch({
      type: ActionTypes.AddProductionPlanChanges,
      payload: { updatedProductionRecords },
    });
    updatePlanProductionValues(updatedProductionRecords);
  };

  return (
    <EditProductionFormActionsContext.Provider value={dispatch}>
      <EditProductionFormContext.Provider value={state}>
        <ProductionGrid
          isReadOnly={isReadOnly}
          detailsByRecipeCategory={state.detailsByRecipeCategory}
          hookDispatch={dispatch}
          prodDetails={FormSelectors.getProdDetails(state)}
          feedingFigure={FormSelectors.getFeedingFigure(state)}
          feedingFigureAlacarte={FormSelectors.getFeedingFigureAlacarte(state)}
          feedingFigureAdult={FormSelectors.getFeedingFigureAdult(state)}
        />
        <div className="menu-toolbox-container ep-toolbox">
          <Toolbox
            {...toolboxProps}
            rightButtons={[
              ...toolboxProps.rightButtons,
              {
                content: 'Save',
                disabled: isReadOnly,
                onClick: () => {
                  saveProdRecords(state.prodRecords);
                },
              },
            ]}
          />
        </div>
        <ProductionPlanModal
          feedingFigure={FormSelectors.getFeedingFigure(state)}
          modalProps={{
            onClose: () => {
              changeModalVisiblity(Modals.ProductionPlan, false);
              changeModalVisiblity(Modals.Close, false);
            },
            open: showProdPlanModal,
          }}
          isReadOnly={isReadOnly}
          showCloseForm={showCloseModal}
          initialValues={FormSelectors.getProductionPlanInitialValues(state)}
          updateForm={updateForm}
          onSubmit={async (formValues) => {
            updateForm(formValues);

            if (showCloseModal) {
              const updatedProdRecord =
                FormHelpers.getUpdatedProdRecordFromProdPlanChanges(
                  state,
                  formValues
                );

              const errors = closeProdFormValidate(formValues, { userName });

              const hasError = !_.isEmpty(errors);

              if (hasError) {
                throw new SubmissionError(errors);
              }

              await closeProdRecord(
                updatedProdRecord,
                FormSelectors.getViewProdRecordIndex(state)
              );
              changeModalVisiblity(Modals.Close, false);
            }
            changeModalVisiblity(Modals.ProductionPlan, false);
          }}
        />
      </EditProductionFormContext.Provider>
    </EditProductionFormActionsContext.Provider>
  );
}

const mapStateToProps = (state, props) => ({
  showProdPlanModal: EditProductionSelectors.getShowProdPlanModal(state),
  showCloseModal: EditProductionSelectors.getShowCloseModal(state),
  userName: CommonStateSelectors.getUserName(state),
});

const mapDispatchToProps = {
  changeModalVisiblity: EditProductionActions.changeModalVisiblity,
  removeAddedLeftovers: EditProductionActions.removeAddedLeftovers,
  updatePlanProductionValues: EditProductionActions.updatePlanProductionValues,
};

const ConnectedForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(EditProductionForm);

export {
  ConnectedForm as EditProductionForm,
  ActionTypes,
  EditProductionFormContext,
  EditProductionFormActionsContext,
};



// WEBPACK FOOTER //
// ./src/pages/manageproduction/subpages/editproduction/components/EditProductionForm.js